"use client";

import { useFormStatus } from "react-dom";
import { useTranslations } from "next-intl";
import { ShoppingBag, Check, ShieldCheck, FileCheck, CheckCircle } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { useAtcStatus } from "@/ui/components/pdp/add-to-cart-form";
import { PaymentIcons } from "@/ui/components/shared/payment-icons";
import { cn } from "@/lib/utils";

interface AddToCartProps {
	disabled?: boolean;
	disabledReason?: "no-selection" | "out-of-stock";
	/**
	 * When true, render only the submit button — the parent (PDP v2 buy-box)
	 * already renders TrustRow + PaymentStrip below the TotalAtcCard, so we
	 * skip the inline duplicates here.
	 */
	minimal?: boolean;
}

function AddToCartButton({
	disabled,
	disabledReason,
}: {
	disabled?: boolean;
	disabledReason?: "no-selection" | "out-of-stock";
}) {
	const t = useTranslations("product");
	// AddToCartForm drives pending/success via context (the form POSTs to a stable
	// route handler, so there's no form action for useFormStatus to track). Fall
	// back to useFormStatus for any legacy form-action usage.
	const atcStatus = useAtcStatus();
	const formStatus = useFormStatus();
	const pending = atcStatus?.pending ?? formStatus.pending;
	const showSuccess = atcStatus?.showSuccess ?? false;

	const getButtonText = () => {
		if (showSuccess) return t("addedToBag");
		if (pending) return t("adding");
		if (!disabled) return t("addToBag");
		if (disabledReason === "out-of-stock") return t("outOfStock");
		return t("selectOptions");
	};

	return (
		<Button
			type="submit"
			data-add-surface="pdp_buybox"
			size="lg"
			disabled={disabled || pending}
			className={cn(
				"h-14 w-full text-base font-medium transition-all duration-200",
				pending && "opacity-80",
				showSuccess && "bg-success hover:bg-success",
			)}
		>
			<span className="t-icon-swap mr-2" data-state={showSuccess ? "b" : "a"} aria-hidden="true">
				<span className="t-icon" data-icon="a">
					<ShoppingBag className={cn("h-5 w-5 transition-transform", pending && "scale-90")} />
				</span>
				<span className="t-icon" data-icon="b">
					<Check className="h-5 w-5" />
				</span>
			</span>
			{getButtonText()}
		</Button>
	);
}

export function AddToCart({ disabled = false, disabledReason, minimal = false }: AddToCartProps) {
	const t = useTranslations("product");

	if (minimal) {
		return <AddToCartButton disabled={disabled} disabledReason={disabledReason} />;
	}

	return (
		<div className="space-y-4">
			{/* Add to Cart Button */}
			<AddToCartButton disabled={disabled} disabledReason={disabledReason} />

			{/* Compact Trust Badges */}
			<div className="grid grid-cols-3 gap-2 pt-2 sm:flex sm:flex-wrap sm:justify-center sm:gap-4">
				<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<ShieldCheck className="h-3.5 w-3.5 shrink-0" />
					{t("trustPurity")}
				</span>
				<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<FileCheck className="h-3.5 w-3.5 shrink-0" />
					{t("trustCoa")}
				</span>
				<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<CheckCircle className="h-3.5 w-3.5 shrink-0" />
					{t("trustGmp")}
				</span>
			</div>

			{/* Payment Methods — centered on desktop, left-aligned on narrow viewports */}
			<PaymentIcons size="sm" className="pt-1 lg:justify-center" />
		</div>
	);
}
